import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import ZohoLogo from '../../shared/assets/svg/zohoLogo.svg';
import MetaLogo from '../../shared/assets/svg/metaLogo.svg';
import Play from '../../shared/assets/svg/play.svg';
import { Sparkles, Globe, CalendarClock } from 'lucide-react';
import {
  getAllCompanies,
  getBuilderWhatsapp,
  getEmailLeadSuggestions
} from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import {
  BUILDER_WHATSAPP_QUERY_KEY,
  isBuilderWhatsappConfigured
} from '../utils/builderWhatsappAccess';
import AiProviderSettingsModal from '../components/forms/AiProviderSettingsModal';
import SchedulingSettingsModal from '../components/forms/SchedulingSettingsModal';
import useSchedulingIntegration from '../hooks/useSchedulingIntegration';
import useEmailLeadSettings from '../hooks/useEmailLeadSettings';
import useEmailSenders from '../hooks/useEmailSenders';
import EmailLogo from '../components/email/EmailLogo';
import { schedulingStatusLabel, providerLabel } from '../utils/schedulingAccess';
import { EMAIL_LEAD_SUGGESTIONS_QUERY_KEY } from '../utils/emailLeadsAccess';
import { EMAIL_HUB_PATH, receivingSummary } from '../utils/emailSendersAccess';
import { getAiProviderSettings } from '../services/api/implementation/impl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const FACEBOOK_OAUTH_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'leads_retrieval',
  'pages_manage_ads',
  'business_management'
].join(',');

const Integration = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const { collapsed } = useSidebar();

  const handleDemoModalToggle = () => {
    setIsDemoModalOpen(!isDemoModalOpen);
  };

  const handleFacebookLogin = () => {
    const appId = '9566903480068710';
    const redirectUri = 'https://bec4-223-185-134-209.ngrok-free.app/facebook/callback';
    const fbLoginUrl = `https://www.facebook.com/v17.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(FACEBOOK_OAUTH_SCOPES)}&response_type=code`;
    window.location.href = fbLoginUrl;
  };

  // Query to get all companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  const {
    data: aiProviderData,
    isPending: isAiSettingsPending
  } = useQuery({
    queryKey: ['aiProviderSettings'],
    queryFn: getAiProviderSettings
  });

  const aiConfigured =
    aiProviderData?.provider?.useOwnKey && aiProviderData?.provider?.apiKeyConfigured;
  const aiUsingPlatform =
    !aiProviderData?.provider?.useOwnKey ||
    aiProviderData?.provider?.provider === 'platform';
  const aiCreditBalance = aiProviderData?.credits?.creditBalance;
  const aiConfigureLabel = aiConfigured
    ? 'Own key connected'
    : aiProviderData?.provider?.useOwnKey
      ? 'Add API key'
      : aiUsingPlatform && aiCreditBalance != null
        ? `${Number(aiCreditBalance).toLocaleString()} tokens`
        : 'Configure';

  const {
    data: builderWhatsappData,
    isPending: isWhatsappIntegrationPending,
    isError: isWhatsappIntegrationError
  } = useQuery({
    queryKey: BUILDER_WHATSAPP_QUERY_KEY,
    queryFn: getBuilderWhatsapp
  });

  const isWhatsappConfigured =
    !isWhatsappIntegrationError && isBuilderWhatsappConfigured(builderWhatsappData);

  const {
    data: schedulingData,
    isPending: isSchedulingPending,
    isError: isSchedulingError,
    provider: schedulingProvider
  } = useSchedulingIntegration();

  const schedulingLabel = schedulingStatusLabel(schedulingData, {
    isPending: isSchedulingPending,
    isError: isSchedulingError
  });

  const {
    data: emailLeadData,
    isPending: isEmailLeadsPending,
    isError: isEmailLeadsError,
    needsAttention: emailLeadsNeedingAttention
  } = useEmailLeadSettings();

  const emailSenders = useEmailSenders();

  // Only managers can see suggestions; for anyone else this 403s and stays quiet.
  const { data: emailLeadSuggestionData } = useQuery({
    queryKey: EMAIL_LEAD_SUGGESTIONS_QUERY_KEY,
    queryFn: getEmailLeadSuggestions,
    retry: false
  });
  const unroutedSuggestions = emailLeadSuggestionData?.suggestions?.length ?? 0;

  useEffect(() => {
    if (searchParams.get('openAi') === '1') {
      setIsAiModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('openAi');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Track page view
  useEffect(() => {
    mixpanel.track('Integration Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (companiesData) {
      setCompanies(companiesData);
    }
  }, [companiesData]);

  const handleViewCompanies = () => {
    navigate('/builder/integration/facebook-companies');
  };

  const handleViewZohoCompanies = () => {
    navigate('/builder/integration/zoho-companies');
  };

  return (
    <Layout>
      {/* Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
        <Header data={{ name: 'Integrations' }} />
      </div>
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        {/* Main Content */}
        <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Zoho Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <img src={ZohoLogo} alt="Zoho Logo" className="h-12 w-auto mb-4 object-contain" />
              <h3 className="font-bold text-lg mb-2 flex gap-2">Zoho</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Easily integrate with Zoho to sync your data and streamline your processes. Whether
                its Zoho CRM, Zoho Projects, or other Zoho apps, our integration ensures smooth
                connectivity and helps you work more efficiently without manual data transfers.
              </p>
              <div className="w-full flex gap-4 justify-between mt-auto">
                <Button displayType="secondary" onClick={handleDemoModalToggle}>
                  <span className="flex gap-1 justify-start items-center">
                    <img src={Play} alt="" className="pt-1" />
                    Demo
                  </span>
                </Button>
                <Button onClick={handleViewZohoCompanies}>View Companies</Button>
              </div>
            </div>

            {/* WhatsApp Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-12 h-12"
                  fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">WhatsApp</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Send notifications, support customers, and run campaigns over the WhatsApp Business
                API. Your WhatsApp account is set up by the Housingram team — contact support to
                enable or update it.
              </p>
              <div className="w-full flex justify-end mt-auto">
                {isWhatsappIntegrationPending ? (
                  <span className="inline-flex items-center justify-center rounded-lg bg-customBlue px-4 py-2 text-sm text-white">
                    Checking…
                  </span>
                ) : isWhatsappConfigured ? (
                  <span className="inline-flex items-center justify-center rounded-lg bg-customBlue px-4 py-2 text-sm text-white">
                    Configured
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center rounded-lg bg-customBlue px-4 py-2 text-sm text-white">
                    Not configured
                  </span>
                )}
              </div>
            </div>

            {/* AI Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-[#1E4A53]">
                  <Sparkles className="h-7 w-7" />
                </div>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">AI Integration</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Manage AI for your builder account: connect your own key from OpenAI, Anthropic
                (Claude), Google (Gemini) or another supported model (recommended when platform
                credits run out), or keep using Housingram&apos;s shared key. Set monthly token caps
                for your sales and operations team.
              </p>
              {aiProviderData?.canManage === false ? (
                <p className="mb-3 text-xs text-amber-800">
                  Only the builder account owner can change API keys and team limits.
                </p>
              ) : null}
              <div className="w-full flex justify-end mt-auto">
                <Button
                  displayType="primary"
                  isLoading={isAiSettingsPending}
                  disabled={isAiSettingsPending}
                  onClick={() => setIsAiModalOpen(true)}>
                  {aiConfigureLabel}
                </Button>
              </div>
            </div>

            {/* Meta Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <img src={MetaLogo} alt="Meta" className="h-12 w-auto mb-4 object-contain" />
              <h3 className="font-bold text-lg mb-2 flex gap-2">Meta</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Easily integrate with Meta to sync data and streamline processes. Whether
                it&apos;s Ads, Facebook Pages, or other tools, our integration ensures smooth
                connectivity and efficient work without manual transfers.
              </p>
              <div className="w-full flex gap-4 justify-between mt-auto">
                <Button displayType="secondary" onClick={handleDemoModalToggle}>
                  <span className="flex gap-1 justify-start items-center">
                    <img src={Play} alt="" className="pt-1" />
                    Demo
                  </span>
                </Button>
                <Button onClick={handleViewCompanies}>View Companies</Button>
              </div>
            </div>

            {/* Google Ads Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-12 h-12">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">Google Ads</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Connect Google Ads to sync lead form and website campaign leads into prelead lists.
                Map campaigns to projects and nurture leads through WhatsApp like other sources.
              </p>
              <div className="w-full flex justify-end mt-auto">
                <Button onClick={() => navigate('/builder/integration/google-ads-companies')}>
                  View Companies
                </Button>
              </div>
            </div>

            {/* Website Forms Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-[#1E4A53]">
                  <Globe className="h-7 w-7" />
                </div>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">Website Forms</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Capture leads from your website forms (e.g. Framer). Create a form, map it to a
                project, and paste its webhook URL into your site — submissions flow straight into
                prelead lists.
              </p>
              <div className="w-full flex justify-end mt-auto">
                <Button onClick={() => navigate('/builder/integration/website-forms')}>
                  Manage Forms
                </Button>
              </div>
            </div>

            {/* Email & Inboxes: sending (company + personal SMTP) and portal lead inboxes */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <EmailLogo />
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">Email &amp; Inboxes</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Send from your company addresses and read the inboxes your 99acres, Housing.com and
                NoBroker lead alerts arrive in.
              </p>
              <dl className="mb-4 w-full space-y-1.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-gray-500">Sending</dt>
                  <dd className="text-gray-900">
                    {emailSenders.isPending ? 'Checking…' : emailSenders.summary}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-gray-500">Lead inbox</dt>
                  <dd className="text-gray-900">
                    {isEmailLeadsPending
                      ? 'Checking…'
                      : isEmailLeadsError
                        ? 'Unavailable'
                        : receivingSummary(emailLeadData?.mailboxes)}
                  </dd>
                </div>
              </dl>
              {emailLeadsNeedingAttention.length > 0 ? (
                <p className="mb-3 text-xs text-amber-800">
                  {emailLeadsNeedingAttention.length === 1
                    ? 'An inbox is not being read right now.'
                    : `${emailLeadsNeedingAttention.length} inboxes are not being read right now.`}
                </p>
              ) : null}
              {unroutedSuggestions > 0 ? (
                <p className="mb-3 text-xs text-amber-800">
                  {unroutedSuggestions === 1
                    ? '1 sender has mail with nowhere to go — pick a pre-lead list for it.'
                    : `${unroutedSuggestions} senders have mail with nowhere to go — pick a pre-lead list for them.`}
                </p>
              ) : null}
              <div className="w-full flex gap-4 justify-between mt-auto">
                <Button
                  displayType="secondary"
                  onClick={() => navigate(`${EMAIL_HUB_PATH}?tab=leads&view=leads`)}>
                  View Leads
                </Button>
                <Button displayType="primary" onClick={() => navigate(EMAIL_HUB_PATH)}>
                  Manage Email
                </Button>
              </div>
            </div>

            {/* Scheduling Integration Card */}
            <div className="bg-white shadow-md p-6 rounded-lg flex flex-col items-start h-full">
              <div className="w-full h-16 mb-4 flex items-center justify-start">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-[#1E4A53]">
                  <CalendarClock className="h-7 w-7" />
                </div>
              </div>
              <h3 className="font-bold text-lg mb-2 flex gap-2">Scheduling</h3>
              <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                Connect WhatsApp booking so leads can book a demo on WhatsApp — they pick a date and
                time in chat, the booking lands on the lead, and a reminder goes out before
                the meeting.
              </p>
              {schedulingData?.connected && schedulingProvider ? (
                <p className="mb-3 text-xs text-gray-500">
                  Using {providerLabel(schedulingProvider)}
                </p>
              ) : null}
              {schedulingData?.connected &&
              !schedulingData?.webhookConfigured &&
              schedulingProvider !== 'pinggo' ? (
                <p className="mb-3 text-xs text-amber-800">
                  Connected, but no webhook is set up — bookings made outside Housingram will not
                  sync back.
                </p>
              ) : null}
              <div className="w-full flex justify-end mt-auto">
                <Button
                  displayType="primary"
                  isLoading={isSchedulingPending}
                  disabled={isSchedulingPending}
                  onClick={() => setIsSchedulingModalOpen(true)}>
                  {schedulingLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAiModalOpen ? (
        <AiProviderSettingsModal onClose={() => setIsAiModalOpen(false)} />
      ) : null}

      {isSchedulingModalOpen ? (
        <SchedulingSettingsModal onClose={() => setIsSchedulingModalOpen(false)} />
      ) : null}

      {/* Demo Modal */}
      {isDemoModalOpen && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name="Zoho Demo"
          onClose={handleDemoModalToggle}
          width={50}>
          <iframe
            width="100%"
            height="315"
            src="https://www.youtube.com/embed/zv0jXPmKBrk"
            title="Zoho Integration Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen></iframe>
        </Modal>
      )}

    </Layout>
  );
};

export default Integration;
